<script lang="ts">
    import type Blocks from '@/schemas/public/Blocks.ts';
    import type { Field, Document, Lead, Comment, Image, SectionLinkType, AuditLog } from '@/types/types';
    import type { DependencyMap } from '@/types/fieldDependency';
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import { router, useForm } from '@inertiajs/svelte';
    import InlineEditable from "@/components/Inputs/InlineEditable.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import AppLayout from "@/layouts/AppLayout.svelte";
    import { Pencil, Plus, Trash, Link, PersonStanding, CirclePoundSterling, ClipboardList, MapPinHouse, FileText, MessageSquare, History, ArrowUpRight } from "lucide-svelte";
    import AuditLogComponent from '@/components/AuditLogComponent.svelte';
    import { Button } from "@/components/ui/button";
    import CreateCustomerModal from "@/components/customers/CreateCustomerModal.svelte";
    import CreateRecordModal from "@/components/CreateRecordModal.svelte";
    import CreateDocumentModal from "@/components/documents/CreateDocumentModal.svelte";
    import RelatedDocuments from "@/components/documents/RelatedDocuments.svelte";
    import CreateJobModal from "@/components/jobs/CreateJobModal.svelte";
    import EditLeadModal from "@/components/leads/EditLeadModal.svelte";
    import LineItems from '@/components/LineItems.svelte';
    import { toast } from 'svelte-sonner';
    import Comments from '@/components/Comments.svelte';
    import { page } from '@inertiajs/svelte';
    import { transformToNestedStructure, transformToFlatStructure } from '@/lib/formTransformers';
    import SiteSurveyForm from '@/components/leads/SiteSurveyForm.svelte';
    import PhasesList from '@/components/phases/PhasesList.svelte';
    import SiteVisitsList from '@/components/siteVisits/SiteVisitsList.svelte';
    import CustomerTable from '@/components/customers/CustomerTable.svelte';
    import LinkExistingModal from '@/components/LinkExistingModal.svelte';

    type Props = {
        lead: Lead;
        blocks: (Blocks & { fields: Field[] })[];
        documentBlocks: (Blocks & { fields: Field[] })[];
        customerBlocks: (Blocks & { fields: Field[] })[];
        jobBlocks: (Blocks & { fields: Field[] })[];
        siteSurveyBlocks: (Blocks & { fields: Field[] })[];
        siteSurveyDependencyMap?: DependencyMap;
        siteVisitBlocks: (Blocks & { fields: Field[] })[];
        siteVisits?: any[];
        relatedDocuments?: Document[];
        comments: Comment[];
        siteSurveyImages?: Image[];
        customerTypeOptions?: Record<string, string>;
        entityFieldColumn?: string;
        auditLogs?: AuditLog[];
    };

    let {
        lead,
        blocks,
        documentBlocks,
        customerBlocks,
        jobBlocks,
        siteSurveyBlocks,
        siteSurveyDependencyMap = {},
        siteVisitBlocks,
        siteVisits = [],
        relatedDocuments,
        comments,
        siteSurveyImages = [],
        customerTypeOptions = {},
        entityFieldColumn,
        auditLogs = []
    }: Props = $props();

    // Get the entity display name using the entityFieldColumn from the module
    const entityDisplayName = entityFieldColumn && (lead as Record<string, any>)[entityFieldColumn]
        ? (lead as Record<string, any>)[entityFieldColumn]
        : 'Lead Details';

    const leadPhases = $derived((lead as any).phases ?? []);

    let breadcrumbs = $state([
        {title: 'Leads', href: route('leads.index')},
        {title: entityDisplayName, href: route('leads.show', {lead: lead.id})},
    ]);

    let deleteForm = $state(useForm({}));

    let actionbuttons = $state([
        {
            title: 'Edit Lead',
            href: undefined,
            onClick: openEditLeadModal,
            icon: Pencil,
        },
        {
            title: 'Add New Lead',
            href: undefined,
            icon: Plus,
            onClick: openCreateLeadModal,
        },
        {
            title: 'Convert to Customer',
            href: undefined,
            icon: Plus,
            onClick: openCreateCustomerModal,
        },
        {
            title: 'Convert to Job',
            href: undefined,
            icon: Plus,
            onClick: openCreateJobModal,
        },
        {
            title: 'Delete Lead',
            actionType: 'delete',
            href: undefined,
            onClick: () => {
                $deleteForm.delete(route('leads.destroy', {lead: lead.id}));
            },
            icon: Trash,
        }
    ]);

    let showCreateLeadModal = $state(false);

    function openCreateLeadModal() {
        showCreateLeadModal = true;
    }

    let editLeadModal = $state(false);

    function openEditLeadModal() {
        editLeadModal = true;
    }

    let showCreateDocumentModal = $state(false);
    function openCreateDocumentModal() {
        showCreateDocumentModal = true;
    }

    let showCreateCustomerModal = $state(false);
    function openCreateCustomerModal(){
        showCreateCustomerModal = true;
    }

    let showCreateJobModal = $state(false);
    function openCreateJobModal(){
        showCreateJobModal = true;
    }


    let inlineFormInner: Record<string, any> = {}
    for (const block of blocks) {
        for (const field of block.fields) {
            inlineFormInner[field.column_name] = field.value;
        }
    }

    // Flatten lead data if it has nested structures
    const flattenedLead = transformToFlatStructure(lead as any);
    inlineFormInner = { ...inlineFormInner, ...flattenedLead };

    // Fix customers to be just IDs if it's an array of objects
    if (lead.customers && Array.isArray(lead.customers)) {
        inlineFormInner['customers'] = lead.customers.map(c => c.id);
    }

    let inlineForm = $state(useForm(inlineFormInner));
    let editingFieldId: undefined | number = $state(undefined);

    let showLinkCustomerModal = $state(false);

    function openLinkCustomerModal() {
        showLinkCustomerModal = true;
    }

    // Section links for quick navigation
    const sectionLinks: SectionLinkType[] = [
        { id: 'section-site-survey', label: 'Site Survey', icon: ClipboardList },
        { id: 'section-customers', label: 'Customers', icon: PersonStanding },
        { id: 'section-line-items', label: 'Line Items', icon: CirclePoundSterling },
        { id: 'section-site-visits', label: 'Site Visits', icon: MapPinHouse },
        { id: 'section-documents', label: 'Documents', icon: FileText },
        { id: 'section-comments', label: 'Comments', icon: MessageSquare },
        { id: 'section-audit-log', label: 'History', icon: History },
    ];

</script>

<svelte:head>
    <title>Lead - {entityDisplayName}</title>
</svelte:head>
<AppLayout {breadcrumbs} {actionbuttons} {sectionLinks}>
    {#if lead.converted_to_job_id && lead.converted_to_job}
        <div class="converted-job-banner">
            <span class="converted-job-label">Converted to Job:</span>
            <Button
                variant="ghost"
                size="sm"
                onclick={() => router.visit(route('jobs.show', { job: lead.converted_to_job_id }))}
                class="converted-job-link"
            >
                {lead.converted_to_job.title || 'View Job'}
                <ArrowUpRight class="h-4 w-4 ml-1" />
            </Button>
        </div>
    {/if}
    <!-- Address Lookup Card -->
<!--    <Card.Root class="mb-4">-->
<!--        <Card.Header>-->
<!--            <Card.Title>Address Lookup</Card.Title>-->
<!--            <Card.Description>Search for an address by postcode</Card.Description>-->
<!--        </Card.Header>-->
<!--        <Card.Content>-->
<!--            <AddressLookup onAddressSelect={handleAddressSelect} />-->
<!--        </Card.Content>-->
<!--    </Card.Root>-->

    <FormContainer>
        {#each blocks.filter(b => (b as any).key !== 'phase_1') as block (block.id)}
            <FormBlock label={block.label}
                       collapsible={true}
                       defaultOpen={block.label !== 'Site Survey'}
            >
                {#each block.fields as field (field.id)}
                    <InlineEditable
                        field={field.column_name === 'status' ? { ...field, editable: false } : field}
                        bind:editingFieldId={editingFieldId}
                        error={$inlineForm.errors[field.column_name]}
                        onSave={(newValue) => {
                                $inlineForm.processing = true;
                                $inlineForm[field.column_name] = newValue;
                                // Transform flat form data to nested structure
                                const transformedData = transformToNestedStructure($inlineForm.data());

                                // This avoids $inlineForm.put() merging original form data (which includes address: null)
                                router.put(route("leads.update", { lead }), transformedData, {
                                    preserveScroll: true,
                                    onSuccess: () => {
                                        editingFieldId = undefined;
                                        toast.success(`Lead updated successfully!`);
                                        $inlineForm.processing = false;
                                    },
                                    onError: (errors) => {
                                        $inlineForm.errors = errors
                                        $inlineForm.processing = false;
                                    }

                                });
                            }
                        }
                        record={lead}
                    />
                {/each}
            </FormBlock>
        {/each}
    </FormContainer>

    <!-- Phases (source of truth for the job's dates) -->
    <PhasesList
        recordType="lead"
        recordId={lead.id}
        phases={leadPhases}
    />

    <!-- Site Survey Block -->
    <SiteSurveyForm
        record={lead}
        recordType="lead"
        {siteSurveyBlocks}
        {siteSurveyDependencyMap}
        {siteSurveyImages}
    />

    {#snippet customerTableHeader()}
        <div class="flex gap-2">
            <Button
                onclick={openLinkCustomerModal}
                size="sm"
                variant="outline"
                class="flex items-center gap-x-2">
                <Link class="h-4 w-4" />
                Link Existing
            </Button>
            <Button
                onclick={openCreateCustomerModal}
                size="sm"
                variant="outline"
                class="flex items-center gap-x-2">
                <Plus class="h-4 w-4" />
                Add Customer
            </Button>
        </div>
    {/snippet}

    <CustomerTable
        id="section-customers"
        view="relatedList"
        relatedData={{
            column: 'related_leads',
            record_id: lead.id
        }}
        title="Related Customers"
        headerSnippet={customerTableHeader}
    />

    <LineItems
        id="section-line-items"
        relatedRecordId={lead.id}
        relatedRecordType="App\Models\Lead"
        lineItems={lead.lineItems}
        reloadKey="lead"
    />

    <!-- Site Visits -->
    <SiteVisitsList
        id="section-site-visits"
        {siteVisits}
        visitableType="App\Models\Lead"
        visitableId={lead.id}
        blocks={siteVisitBlocks}
    />

    <RelatedDocuments id="section-documents" {relatedDocuments} blocks={documentBlocks} onOpenCreate={openCreateDocumentModal}>
        <CreateDocumentModal
            blocks={documentBlocks}
            bind:show={showCreateDocumentModal}
            relatedRecordType="App\Models\Lead"
            relatedRecordId={lead.id}
            relatedRecordLabel={lead.name}
        />
    </RelatedDocuments>

    <Comments
        id="section-comments"
        commentableId={lead.id}
        commentableType="App\Models\Lead"
        {comments}
        currentUser={$page.props.auth.user}
    />

    <AuditLogComponent id="section-audit-log" {auditLogs} />

    <EditLeadModal
        {blocks}
        bind:show={editLeadModal}
        lead={lead}
    />

    <CreateRecordModal
        blocks={blocks}
        bind:show={showCreateLeadModal}
        entityTitle="Lead"
        createRoute="leads.store"
    />

    <CreateCustomerModal
        bind:show={showCreateCustomerModal}
        blocks={customerBlocks}
        {customerTypeOptions}
        initialValues={{
            first_name: lead.first_name,
            last_name: lead.last_name,
            phone: lead.phone,
            email: lead.email,
            organisation_name: lead.organisation_name,
        }}
    />

    <CreateJobModal
        blocks={jobBlocks}
        bind:show={showCreateJobModal}
        initialValues={{
            start_date_time: lead.estimated_job_start,
            end_date_time: lead.estimated_job_end,
            notes: lead.description_of_work,
            site_name: lead.organisation_name,
            address_line_one: lead.address_line_one,
            address_line_two: lead.address_line_two,
            city: lead.city,
            county: lead.county,
            postcode: lead.postcode,
            country: lead.country,
            additional_hire_start_date: lead.additional_hire_start_date,
            additional_hire_end_date: lead.additional_hire_end_date,
            additional_hire_duration: lead.additional_hire_duration,
        }}
    />

    <LinkExistingModal
        bind:show={showLinkCustomerModal}
        entityTitle="Customers"
        TableComponent={CustomerTable}
        relatedRecordId={lead.id}
        relationshipName="customers"
        syncRoute="leads.update"
        currentlyRelated={lead.customers}
        recordData={inlineFormInner}
        routeParamName="lead"
    />

</AppLayout>

<style>
    .converted-job-banner {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        margin-bottom: 1rem;
        background: var(--clr-surface-2);
        border-radius: 0.5rem;
        border: 1px solid var(--clr-border);
    }

    .converted-job-label {
        font-size: 0.875rem;
        color: var(--clr-on-surface-2);
    }

    :global(.converted-job-link) {
        font-weight: 500;
        color: var(--link);
    }

    :global(.converted-job-link:hover) {
        text-decoration: underline;
    }
</style>
